import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../config/firebase-config';
import { SUPER_ADMIN_EMAILS } from '../../config/permissions';

const GlobalAdmins = () => {
    const [adminUsers, setAdminUsers] = useState([]);
    const [orgNames, setOrgNames] = useState({}); // organization id -> name, so each admin is tagged with the org they administer
    const [isLoading, setIsLoading] = useState(true);
    // Revoking access asks twice: `pending` is the row being revoked and which of the two confirmations is open.
    const [pending, setPending] = useState(null); // { id, step: 1 | 2 }
    const [busyId, setBusyId] = useState(null);
    const [notice, setNotice] = useState(null); // { kind: 'ok' | 'error', text }

    const isPlatformOwner = (email) => SUPER_ADMIN_EMAILS.includes(String(email || '').toLowerCase());
    const orgLabel = (orgId) => (orgId ? (orgNames[orgId] || orgId) : 'the platform');

    // Revoke: the role goes back to member, and the email comes off the organization's admin list,
    // since syncMemberRole would otherwise hand the role straight back at the next sign-in.
    // The account, its check-ins and any coach seat on a roster are untouched.
    const revokeAccess = async (admin) => {
        setBusyId(admin.id); setNotice(null);
        try {
            const email = String(admin.email || '').toLowerCase().trim();
            if (admin.organizationId && email) {
                const orgRef = doc(db, 'organizations', admin.organizationId);
                const orgSnap = await getDoc(orgRef);
                if (orgSnap.exists()) {
                    const list = Array.isArray(orgSnap.data().adminEmails) ? orgSnap.data().adminEmails : [];
                    const kept = list.filter(e => String(e || '').toLowerCase().trim() !== email);
                    if (kept.length !== list.length) await updateDoc(orgRef, { adminEmails: kept });
                }
            }
            await updateDoc(doc(db, 'users', admin.id), {
                role: 'user',
                roleRevokedAt: new Date().toISOString(),
                roleRevokedBy: auth.currentUser?.email || null,
            });
            setAdminUsers(rows => rows.filter(r => r.id !== admin.id));
            setNotice({ kind: 'ok', text: `Admin access revoked for ${admin.email || admin.name || admin.id}. They keep their account as a regular member.` });
        } catch (error) {
            console.error('Revoke error:', error);
            setNotice({ kind: 'error', text: `Could not revoke access for ${admin.email || admin.id}: ${error.message || error}` });
        } finally {
            setBusyId(null); setPending(null);
        }
    };

    useEffect(() => {
        const fetchAdmins = async () => {
            const q = query(collection(db, "users"), where("role", "in", ["admin", "super_admin"]));
            try {
                const [snapshot, orgs] = await Promise.all([getDocs(q), getDocs(collection(db, "organizations"))]);
                const names = {};
                orgs.docs.forEach(d => { names[d.id] = d.data().name || d.id; });
                setOrgNames(names);
                // Grouped by organization (name order), super admins first within an org.
                const rows = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                rows.sort((a, b) => {
                    const an = a.organizationId ? (names[a.organizationId] || a.organizationId) : '';
                    const bn = b.organizationId ? (names[b.organizationId] || b.organizationId) : '';
                    if (an !== bn) return an === '' ? -1 : bn === '' ? 1 : an.localeCompare(bn);
                    if (a.role !== b.role) return a.role === 'super_admin' ? -1 : 1;
                    return String(a.name || a.email || '').localeCompare(String(b.name || b.email || ''));
                });
                setAdminUsers(rows);
            } catch (error) { console.error("Admin fetch error:", error); }
            finally { setIsLoading(false); }
        };
        fetchAdmins();
    }, []);

    // The tag: the organization this person administers, by name, with the id underneath; "Global" when the record has none.
    const OrgTag = ({ orgId }) => {
        if (!orgId) return <span className="px-2 py-1 rounded text-xs font-bold uppercase bg-amber-100 text-amber-700">Global</span>;
        const name = orgNames[orgId];
        return (
            <div className="flex flex-col gap-1">
                <span className={`inline-block w-fit px-2 py-1 rounded text-xs font-bold ${name ? 'bg-slate-100 text-slate-700' : 'bg-rose-100 text-rose-700'}`}>{name || 'Unknown organization'}</span>
                <span className="text-[10px] text-slate-400 font-mono">{orgId}</span>
            </div>
        );
    };

    // The two confirmations, inline under the row: first what will happen, then a last check. No browser dialogs.
    const ConfirmRow = ({ admin }) => {
        const who = admin.name ? `${admin.name} (${admin.email})` : admin.email;
        const step = pending?.step || 1;
        return (
            <tr className="bg-rose-50">
                <td colSpan="5" className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
                        {step === 1 ? (
                            <div className="text-sm text-slate-700">
                                <p className="font-bold text-rose-700">Revoke the admin access of {who} to {orgLabel(admin.organizationId)}?</p>
                                <p className="mt-1">They keep their account and their check-ins and become a regular member. Their email also comes off the organization's admin list, so the role is not handed back at their next sign-in. A coach seat on a team roster is not affected.</p>
                            </div>
                        ) : (
                            <div className="text-sm text-slate-700">
                                <p className="font-bold text-rose-700">Last check: revoke now?</p>
                                <p className="mt-1">This takes effect immediately. To restore access later, add {admin.email} back to the organization's admin list in the organization editor.</p>
                            </div>
                        )}
                        <div className="flex gap-2 flex-none">
                            <button type="button" onClick={() => setPending(null)} disabled={busyId === admin.id} className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50">Cancel</button>
                            {step === 1 ? (
                                <button type="button" onClick={() => setPending({ id: admin.id, step: 2 })} className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-rose-600 hover:bg-rose-700">Continue</button>
                            ) : (
                                <button type="button" onClick={() => revokeAccess(admin)} disabled={busyId === admin.id} className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-rose-700 hover:bg-rose-800 disabled:opacity-60">{busyId === admin.id ? 'Revoking…' : 'Yes, revoke access'}</button>
                            )}
                        </div>
                    </div>
                </td>
            </tr>
        );
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            {notice && (
                <div className={`px-4 py-3 text-sm font-semibold border-b ${notice.kind === 'ok' ? 'bg-emerald-50 text-emerald-800 border-emerald-100' : 'bg-rose-50 text-rose-800 border-rose-100'}`}>{notice.text}</div>
            )}
            {isLoading ? (<div className="p-12 text-center text-slate-400">Loading directory...</div>) : (
                <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                            <th className="p-4 text-xs font-bold text-slate-400 uppercase">Name</th>
                            <th className="p-4 text-xs font-bold text-slate-400 uppercase">Email</th>
                            <th className="p-4 text-xs font-bold text-slate-400 uppercase">Role</th>
                            <th className="p-4 text-xs font-bold text-slate-400 uppercase">Admin of</th>
                            <th className="p-4 text-xs font-bold text-slate-400 uppercase text-right">Access</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {adminUsers.map(admin => (
                            <React.Fragment key={admin.id}>
                                <tr className="hover:bg-slate-50 transition-colors">
                                    <td className="p-4 font-bold text-slate-700">{admin.name || "Unknown"}</td>
                                    <td className="p-4 text-sm text-slate-600 font-mono">{admin.email}</td>
                                    <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-bold uppercase ${admin.role === 'super_admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{admin.role}</span></td>
                                    <td className="p-4"><OrgTag orgId={admin.organizationId} /></td>
                                    <td className="p-4 text-right">
                                        {isPlatformOwner(admin.email) ? (
                                            <span className="text-xs font-semibold text-slate-400">Platform owner</span>
                                        ) : (
                                            <button type="button" onClick={() => setPending({ id: admin.id, step: 1 })} disabled={!!busyId || pending?.id === admin.id} className="px-3 py-1.5 rounded-lg text-xs font-bold text-rose-700 bg-white border border-rose-200 hover:bg-rose-50 disabled:opacity-50">Revoke access</button>
                                        )}
                                    </td>
                                </tr>
                                {pending?.id === admin.id && <ConfirmRow admin={admin} />}
                            </React.Fragment>
                        ))}
                        {adminUsers.length === 0 && (<tr><td colSpan="5" className="p-8 text-center text-slate-400">No admin users found.</td></tr>)}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default GlobalAdmins;